import React, { useContext, useEffect, useState } from 'react'
import { Select, Button } from 'antd'
import MonacoEditor from '../../../../helpers/CodeEditor2'
import { ReactComponent as RunIcon } from '../../../../assets/svg/MarketplaceFunctions/run.svg'
import { useCreateFunction, setExecMode, logs } from './CreateFunctionContext'
import {
  mapPermissions,
  getCheckedEntries,
  mapContext,
  evalFnInClient,
  evalFnInServer,
} from './helpers/common'
import { bootstrapCode } from './helpers/execution'
import { CreateMarketplaceFn } from '../../../../helpers/RxServices'
import { encodeBase64, getComSource } from '../../../../helpers/commonFunction'
import { ExecutionMode } from './types'
import style from './styles/create-function.module.css'

const { Option } = Select

interface IHeader {
  terminalRef: any
  editorRef: any
  isRelease: boolean
  setIsRelease: (isRelease: boolean) => void
  setIsExecuting: (isExecuting: boolean) => void
}

const Header: React.FC<IHeader> = (props) => {
  const { editorRef, setIsExecuting, isRelease, setIsRelease } = props
  const { state, dispatch } = useCreateFunction()
  const { executionMode, metadata } = state
  
  const handleRelease = () => {
    setIsRelease(!isRelease)
    if (!isRelease) {
      editorRef.current.setValue(state.source)
    }
  }

  const executeCode = async () => {
    const source = editorRef.current.getValue()
    const context = mapContext(state.context)
    const permissions = mapPermissions(state.permissions)
    const imports = getCheckedEntries(state.imports)

    const getCodeByMode = (isClient: boolean) => {
      return bootstrapCode(context, source, isClient, permissions)
    }

    const payload = {
      imports,
      context,
      code: encodeBase64(getCodeByMode(false)),
    }

    setIsExecuting(true)
    try {
      const execRes: any =
        executionMode === 'server'
          ? await evalFnInServer(payload)
          : await evalFnInClient(getCodeByMode(true))
      if (execRes.result === 'success') {
        dispatch(logs(execRes.output))

      } else {
        dispatch(logs(execRes.error))

      }
    } catch (err) {
      dispatch(logs(`failed to exec fn: ${err}`))
    } finally {
      setIsExecuting(false)
    }
  }

  return (
    <div className={style.tabPaneHeader}>
      <Select
        disabled={Boolean(metadata.id)}
        style={{ width: 200 }}
        value={state.executionMode}
        onChange={(val: ExecutionMode) => dispatch(setExecMode(val))}
      >
        <Option value="client">Client</Option>
        <Option value="server">Server</Option>
      </Select>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        {metadata.id ? (
          <Button
            size="small"
            onClick={handleRelease}
            type={isRelease ? 'primary' : 'danger'}
          >
            {isRelease ? 'Create Release' : 'Cancel'}
          </Button>
        ) : null}
        <RunIcon
          height={28}
          width={28}
          style={{ cursor: 'pointer' }}
          onClick={executeCode}
        />
      </div>
    </div>
  )
}

interface IEditor {
  terminalRef: any
  setIsExecuting: (isExecuting: boolean) => void
  editorRef: any
  isRelease: boolean
  setIsRelease: (isRelease: boolean) => void
}

export const Editor: React.FC<IEditor> = (props) => {
  const { terminalRef, editorRef, setIsExecuting, isRelease, setIsRelease } = props
  const { state } = useCreateFunction()
  const source = state.source

  useEffect(() => {
    const subscription = CreateMarketplaceFn.getMessage().subscribe(
      (data: any) => {
        if (data.message === 'requestContext') {
          const content = editorRef.current.getValue()
          const comSource = getComSource(content)
          const context = mapContext(state.context)
          const permissions = mapPermissions(state.permissions)
          CreateMarketplaceFn.sendMessage({
            message: 'sendContext',
            data: {
              content,
              comSource,
              context,
              permissions,
              executionMode: state.executionMode,
              metadata: state.metadata,
            },
          })
        }
      }
    )
    return () => subscription.unsubscribe()
  }, [state, editorRef])

  return (
    <div className="mp-func-editor">
      <Header
        setIsRelease={setIsRelease}
        isRelease={isRelease}
        terminalRef={terminalRef}
        editorRef={editorRef}
        setIsExecuting={setIsExecuting}
      />
      <MonacoEditor
        editorRef={editorRef}
        minimap={false}
        value={source}
        fontSize={16}
        height="100%"
        type="functionClient"
        lang="javascript"
        isLangSelVisible={false}
        readOnly={isRelease}
        additionalOptions={{
          overviewRulerLanes: 0,
          overviewRulerBorder: false,
          scrollbar: {
            horizontal: 'hidden',
          },
        }}
      />
    </div>
  )
}
